/**
 * iter649 (extended iter652) — Sales & Tax Report page.
 *
 * Three windows from one engine:
 *  - Monthly  → year + month picker
 *  - Quarterly (VAT-3) → year + Q1/Q2/Q3/Q4
 *  - Custom range → from-date + to-date pickers
 *
 * Each tab shares the same Stats grid + payment-method table +
 * voided-orders table, and downloads its own PDF.
 */
import { useEffect, useState } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { FileText, Download, BarChart3, AlertCircle, FileSpreadsheet, Mail, Send, X } from 'lucide-react';
import api from '@/lib/api';
import { useBranchStore } from '@/stores';
import { getBrowserTz, localToday, localMonthStart } from '@/lib/dateRange';

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

export default function MonthlyTaxReportPage() {
  const branches = useBranchStore(s => s.branches);
  const today = new Date();

  // Window state — one per tab.
  const [tab, setTab] = useState('month');
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [quarter, setQuarter] = useState(Math.floor(today.getMonth() / 3) + 1);
  const [dateFrom, setDateFrom] = useState(localMonthStart());
  const [dateTo, setDateTo] = useState(localToday());
  const [branchId, setBranchId] = useState('');

  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const buildParams = () => {
    const p = new URLSearchParams({ tz: getBrowserTz() });
    if (branchId) p.set('branch_id', branchId);
    if (tab === 'month') {
      p.set('year', String(year));
      p.set('month', String(month));
    } else if (tab === 'quarter') {
      p.set('year', String(year));
      p.set('quarter', String(quarter));
    } else {
      p.set('date_from', dateFrom);
      p.set('date_to', dateTo);
    }
    return p;
  };

  const endpoint = (kind) => {
    const base = tab === 'month' ? 'monthly' : tab === 'quarter' ? 'quarterly' : 'range';
    return `/tax-reports/${base}-${kind}`;
  };

  const fetchSummary = async () => {
    setLoading(true);
    setSummary(null);
    try {
      const { data } = await api.get(`${endpoint('summary')}?${buildParams().toString()}`);
      setSummary(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load summary');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSummary(); }, [tab, year, month, quarter, dateFrom, dateTo, branchId]);

  // Note: downloadPdf intentionally not memoised — it's a one-shot button
  // handler. setState calls here run on click (event), not inside an
  // effect; the local eslint rule false-positives on this pattern.
  async function downloadPdf() {
    setDownloading(true);
    try {
      const res = await api.get(`${endpoint('pdf')}?${buildParams().toString()}`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      const fname =
        tab === 'month' ? `monthly-tax-report-${year}-${String(month).padStart(2, '0')}.pdf`
        : tab === 'quarter' ? `quarterly-tax-report-${year}-Q${quarter}.pdf`
        : `tax-report-${dateFrom}_to_${dateTo}.pdf`;
      a.download = fname;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to download PDF');
    } finally {
      setDownloading(false);
    }
  }

  function downloadCsv() {
    setDownloading(true);
    (async () => {
      try {
        const base = tab === 'month' ? 'monthly' : tab === 'quarter' ? 'quarterly' : 'range';
        const res = await api.get(`/tax-reports/${base}-csv?${buildParams().toString()}`, { responseType: 'blob' });
        const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
        const a = document.createElement('a');
        a.href = url;
        a.download =
          tab === 'month' ? `tax-report-${year}-${String(month).padStart(2, '0')}.csv`
          : tab === 'quarter' ? `tax-report-${year}-Q${quarter}.csv`
          : `tax-report-${dateFrom}_to_${dateTo}.csv`;
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(url);
        toast.success('CSV downloaded');
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to download CSV');
      } finally {
        setDownloading(false);
      }
    })();
  }

  // iter654 — Email settings (lazy-load on first open).
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailSettings, setEmailSettings] = useState(null);
  const [emailRecipientInput, setEmailRecipientInput] = useState('');
  const [emailSaving, setEmailSaving] = useState(false);
  const [emailSending, setEmailSending] = useState(false);
  async function openEmailDialog() {
    setEmailDialogOpen(true);
    try {
      const { data } = await api.get('/tax-reports/email-settings');
      setEmailSettings({
        recipients: data.recipients || [],
        monthly_enabled: !!data.monthly_enabled,
        quarterly_enabled: !!data.quarterly_enabled,
        tz: data.tz || 'Asia/Riyadh',
        last_sent_monthly: data.last_sent_monthly || null,
        last_sent_quarterly: data.last_sent_quarterly || null,
      });
    } catch (err) {
      toast.error('Failed to load email settings');
    }
  }
  async function saveEmailSettings() {
    if (!emailSettings) return;
    setEmailSaving(true);
    try {
      const { data } = await api.put('/tax-reports/email-settings', {
        recipients: emailSettings.recipients,
        monthly_enabled: emailSettings.monthly_enabled,
        quarterly_enabled: emailSettings.quarterly_enabled,
        tz: emailSettings.tz,
      });
      setEmailSettings(s => ({ ...s, ...data }));
      toast.success('Email settings saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setEmailSaving(false);
    }
  }
  async function sendTestNow() {
    setEmailSending(true);
    try {
      const payload = { kind: tab };
      if (tab === 'month') Object.assign(payload, { year, month });
      else if (tab === 'quarter') Object.assign(payload, { year, quarter });
      else Object.assign(payload, { date_from: dateFrom, date_to: dateTo });
      const { data } = await api.post('/tax-reports/send-now', payload);
      toast.success(`Sent to ${data.recipients.join(', ')}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send');
    } finally {
      setEmailSending(false);
    }
  }

  const yearOptions = [];
  for (let y = today.getFullYear(); y >= today.getFullYear() - 5; y--) yearOptions.push(y);

  const cur = summary?.currency || 'SAR';
  const t = summary?.totals || {};

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto px-4 py-6 space-y-5" data-testid="monthly-tax-report-page">
        <header className="flex items-start gap-3 justify-between flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-stone-800 to-black flex items-center justify-center shadow-md">
              <FileText size={22} className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold font-outfit">Sales &amp; Tax Report</h1>
              <p className="text-sm text-muted-foreground">Monthly · Quarterly (VAT-3) · Custom range — all with voided-orders evidence.</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            <Button variant="outline" size="sm" onClick={downloadCsv} disabled={downloading || loading || !summary} data-testid="download-csv-btn" title="Download data as machine-readable CSV (for Excel / accounting software)">
              <FileSpreadsheet size={14} className="mr-1.5" /> CSV
            </Button>
            <Button variant="outline" size="sm" onClick={openEmailDialog} data-testid="email-settings-btn" title="Email this report to the accountant on the 1st of every month / quarter">
              <Mail size={14} className="mr-1.5" /> Email settings
            </Button>
            <Button onClick={downloadPdf} disabled={downloading || loading || !summary} data-testid="download-pdf-btn">
              <Download size={14} className="mr-1.5" /> {downloading ? 'Generating…' : 'Download PDF'}
            </Button>
          </div>
        </header>

        <Tabs value={tab} onValueChange={setTab} data-testid="tax-report-tabs">
          <TabsList className="w-full">
            <TabsTrigger value="month" className="flex-1" data-testid="tab-month">Monthly</TabsTrigger>
            <TabsTrigger value="quarter" className="flex-1" data-testid="tab-quarter">Quarterly (VAT-3)</TabsTrigger>
            <TabsTrigger value="range" className="flex-1" data-testid="tab-range">Custom Range</TabsTrigger>
          </TabsList>

          {/* Filters per tab */}
          <Card className="mt-3">
            <CardContent className="p-3 flex items-center gap-2 flex-wrap" data-testid="filter-row">
              <div className="flex items-center gap-1 text-xs">
                <label className="text-muted-foreground">Branch:</label>
                <select value={branchId} onChange={(e) => setBranchId(e.target.value)} className="h-8 px-2 rounded border border-input bg-background text-xs" data-testid="branch-filter">
                  <option value="">All branches</option>
                  {(branches || []).map(b => (<option key={b.id} value={b.id}>{b.name}</option>))}
                </select>
              </div>

              <TabsContent value="month" className="m-0 flex items-center gap-2 flex-wrap" forceMount hidden={tab !== 'month'}>
                <div className="flex items-center gap-1 text-xs">
                  <label className="text-muted-foreground">Year:</label>
                  <select value={year} onChange={(e) => setYear(Number(e.target.value))} className="h-8 px-2 rounded border border-input bg-background text-xs" data-testid="year-filter">
                    {yearOptions.map(y => (<option key={y} value={y}>{y}</option>))}
                  </select>
                </div>
                <div className="flex items-center gap-1 text-xs">
                  <label className="text-muted-foreground">Month:</label>
                  <select value={month} onChange={(e) => setMonth(Number(e.target.value))} className="h-8 px-2 rounded border border-input bg-background text-xs" data-testid="month-filter">
                    {MONTH_NAMES.map((n, i) => (<option key={i + 1} value={i + 1}>{n}</option>))}
                  </select>
                </div>
              </TabsContent>

              <TabsContent value="quarter" className="m-0 flex items-center gap-2 flex-wrap" forceMount hidden={tab !== 'quarter'}>
                <div className="flex items-center gap-1 text-xs">
                  <label className="text-muted-foreground">Year:</label>
                  <select value={year} onChange={(e) => setYear(Number(e.target.value))} className="h-8 px-2 rounded border border-input bg-background text-xs" data-testid="year-filter-q">
                    {yearOptions.map(y => (<option key={y} value={y}>{y}</option>))}
                  </select>
                </div>
                <div className="flex items-center gap-1 text-xs">
                  <label className="text-muted-foreground">Quarter:</label>
                  <select value={quarter} onChange={(e) => setQuarter(Number(e.target.value))} className="h-8 px-2 rounded border border-input bg-background text-xs" data-testid="quarter-filter">
                    <option value={1}>Q1 (Jan–Mar)</option>
                    <option value={2}>Q2 (Apr–Jun)</option>
                    <option value={3}>Q3 (Jul–Sep)</option>
                    <option value={4}>Q4 (Oct–Dec)</option>
                  </select>
                </div>
              </TabsContent>

              <TabsContent value="range" className="m-0 flex items-center gap-2 flex-wrap" forceMount hidden={tab !== 'range'}>
                <div className="flex items-center gap-1 text-xs">
                  <label className="text-muted-foreground">From:</label>
                  <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-8 w-[140px] text-xs" data-testid="range-from" />
                </div>
                <div className="flex items-center gap-1 text-xs">
                  <label className="text-muted-foreground">To:</label>
                  <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-8 w-[140px] text-xs" data-testid="range-to" />
                </div>
              </TabsContent>
            </CardContent>
          </Card>
        </Tabs>

        {loading && <Card><CardContent className="p-12 text-center text-muted-foreground">Loading…</CardContent></Card>}

        {summary && !loading && (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2.5" data-testid="totals-row">
              <Stat label="Gross" value={`${cur} ${(t.gross || 0).toFixed(2)}`} testId="stat-gross" />
              <Stat label="Discount" value={`– ${cur} ${(t.discount || 0).toFixed(2)}`} cls="text-amber-700" testId="stat-discount" />
              <Stat label="VAT" value={`${cur} ${(t.tax || 0).toFixed(2)}`} cls="text-sky-700" testId="stat-tax" />
              <Stat label="Total" value={`${cur} ${(t.total || 0).toFixed(2)}`} cls="text-emerald-700" testId="stat-total" />
              <Stat label="Voided" value={`${t.voided_count || 0} (${cur} ${(t.voided_total || 0).toFixed(2)})`} cls="text-rose-700" testId="stat-voided" />
            </div>

            <Card>
              <CardHeader><CardTitle className="text-base font-outfit flex items-center gap-2"><BarChart3 size={15} /> Payment-method breakdown</CardTitle></CardHeader>
              <CardContent className="p-0 overflow-x-auto">
                <table className="w-full text-sm" data-testid="payment-table">
                  <thead><tr className="bg-stone-50 dark:bg-stone-900/60 text-[11px] uppercase tracking-wide text-muted-foreground">
                    <th className="text-left px-3 py-2 font-semibold">Method</th>
                    <th className="text-right px-3 py-2 font-semibold"># Orders</th>
                    <th className="text-right px-3 py-2 font-semibold">Total</th>
                  </tr></thead>
                  <tbody>
                    {(summary.payment_methods || []).map((p, i) => (
                      <tr key={i} className="border-t border-border/60">
                        <td className="px-3 py-2 capitalize">{p.method}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{p.count}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{cur} {p.total.toFixed(2)}</td>
                      </tr>
                    ))}
                    {(summary.payment_methods || []).length === 0 && (
                      <tr><td colSpan={3} className="px-3 py-6 text-center text-muted-foreground text-xs">No payments in this period.</td></tr>
                    )}
                  </tbody>
                </table>
              </CardContent>
            </Card>

            {summary.voided_orders?.length > 0 && (
              <Card>
                <CardHeader><CardTitle className="text-base font-outfit flex items-center gap-2 text-rose-700"><AlertCircle size={15} /> Voided orders (retained for audit)</CardTitle></CardHeader>
                <CardContent className="p-0 overflow-x-auto">
                  <table className="w-full text-sm" data-testid="voided-table">
                    <thead><tr className="bg-rose-50 dark:bg-rose-950/30 text-[11px] uppercase tracking-wide text-rose-800">
                      <th className="text-left px-3 py-2 font-semibold">Order #</th>
                      <th className="text-left px-3 py-2 font-semibold">Voided at (UTC)</th>
                      <th className="text-left px-3 py-2 font-semibold">By</th>
                      <th className="text-right px-3 py-2 font-semibold">Original total</th>
                    </tr></thead>
                    <tbody>
                      {summary.voided_orders.map((v, i) => (
                        <tr key={i} className="border-t border-rose-100">
                          <td className="px-3 py-2">{v.order_number || '—'}</td>
                          <td className="px-3 py-2 text-xs text-muted-foreground">{v.voided_at?.replace('T', ' ').split('.')[0] || '—'}</td>
                          <td className="px-3 py-2 text-xs text-muted-foreground truncate max-w-[180px]">{v.voided_by || '—'}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{cur} {v.total.toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            )}

            <p className="text-[11px] text-muted-foreground text-center max-w-2xl mx-auto">
              All POS sale invoices (including voided ones) are retained for a minimum of 5 years in accordance with the tax authority&apos;s electronic-invoice record-keeping requirements.
            </p>
          </>
        )}
      </div>

      {/* iter654 — Email settings dialog */}
      {emailDialogOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
          onClick={() => setEmailDialogOpen(false)}
          data-testid="email-settings-dialog-overlay"
        >
          <div
            className="bg-background rounded-2xl shadow-2xl max-w-md w-full p-5 max-h-[90dvh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="email-settings-dialog"
          >
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-bold text-base font-outfit flex items-center gap-2">
                <Mail size={16} className="text-orange-500" /> Email this report
              </h2>
              <button onClick={() => setEmailDialogOpen(false)} className="text-muted-foreground hover:text-foreground" data-testid="email-settings-close">
                <X size={16} />
              </button>
            </div>
            {!emailSettings ? (
              <p className="text-sm text-muted-foreground py-6 text-center">Loading…</p>
            ) : (
              <>
                <p className="text-xs text-muted-foreground mb-3">
                  We&apos;ll send the PDF + CSV to these addresses automatically. Monthly fires on the 1st of every local month; quarterly on the 1st of Jan / Apr / Jul / Oct.
                </p>
                <label className="text-xs font-medium block mb-1">Recipients</label>
                <div className="flex flex-wrap gap-1.5 mb-2 min-h-[28px] p-1.5 border border-input rounded-lg" data-testid="email-recipients-chips">
                  {(emailSettings.recipients || []).length === 0 && (
                    <span className="text-[11px] text-muted-foreground italic px-1">No recipients yet</span>
                  )}
                  {(emailSettings.recipients || []).map((r, i) => (
                    <span key={i} className="inline-flex items-center gap-1 bg-muted rounded-full px-2 py-0.5 text-[11px]" data-testid={`recipient-chip-${i}`}>
                      {r}
                      <button
                        onClick={() => setEmailSettings(s => ({ ...s, recipients: s.recipients.filter((_, j) => j !== i) }))}
                        className="hover:text-rose-600"
                        data-testid={`remove-recipient-${i}`}
                      >
                        <X size={10} />
                      </button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-1 mb-3">
                  <Input
                    type="email"
                    placeholder="accountant@example.com"
                    value={emailRecipientInput}
                    onChange={(e) => setEmailRecipientInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && emailRecipientInput.trim()) {
                        e.preventDefault();
                        const v = emailRecipientInput.trim().toLowerCase();
                        if (v.includes('@') && v.includes('.')) {
                          setEmailSettings(s => ({ ...s, recipients: s.recipients.includes(v) ? s.recipients : [...s.recipients, v] }));
                          setEmailRecipientInput('');
                        } else {
                          toast.error('Invalid email address');
                        }
                      }
                    }}
                    className="h-8 text-xs"
                    data-testid="email-recipient-input"
                  />
                  <Button size="sm" variant="outline" className="h-8 text-xs"
                    onClick={() => {
                      const v = emailRecipientInput.trim().toLowerCase();
                      if (!v) return;
                      if (!v.includes('@') || !v.includes('.')) return toast.error('Invalid email address');
                      setEmailSettings(s => ({ ...s, recipients: s.recipients.includes(v) ? s.recipients : [...s.recipients, v] }));
                      setEmailRecipientInput('');
                    }}
                    data-testid="add-recipient-btn"
                  >Add</Button>
                </div>

                <div className="space-y-2 mb-4 text-xs">
                  <label className="flex items-center gap-2 cursor-pointer" data-testid="toggle-monthly-wrapper">
                    <input
                      type="checkbox"
                      checked={emailSettings.monthly_enabled}
                      onChange={(e) => setEmailSettings(s => ({ ...s, monthly_enabled: e.target.checked }))}
                      className="w-3.5 h-3.5 accent-orange-500"
                      data-testid="toggle-monthly"
                    />
                    Monthly — send on the 1st of each month
                    {emailSettings.last_sent_monthly && (
                      <span className="text-[10px] text-muted-foreground ml-1">· last: {emailSettings.last_sent_monthly}</span>
                    )}
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer" data-testid="toggle-quarterly-wrapper">
                    <input
                      type="checkbox"
                      checked={emailSettings.quarterly_enabled}
                      onChange={(e) => setEmailSettings(s => ({ ...s, quarterly_enabled: e.target.checked }))}
                      className="w-3.5 h-3.5 accent-orange-500"
                      data-testid="toggle-quarterly"
                    />
                    Quarterly (VAT-3) — Jan / Apr / Jul / Oct
                    {emailSettings.last_sent_quarterly && (
                      <span className="text-[10px] text-muted-foreground ml-1">· last: {emailSettings.last_sent_quarterly}</span>
                    )}
                  </label>
                </div>

                <div className="flex items-center justify-between gap-2 border-t pt-3">
                  <Button
                    variant="outline" size="sm"
                    disabled={emailSending || (emailSettings.recipients || []).length === 0}
                    onClick={sendTestNow}
                    className="text-xs"
                    data-testid="send-test-now-btn"
                    title="Send this report (currently selected window) immediately to the recipients above"
                  >
                    <Send size={12} className="mr-1.5" />
                    {emailSending ? 'Sending…' : 'Send test now'}
                  </Button>
                  <Button
                    size="sm"
                    onClick={saveEmailSettings}
                    disabled={emailSaving}
                    className="bg-orange-500 hover:bg-orange-600 text-white text-xs"
                    data-testid="save-email-settings-btn"
                  >
                    {emailSaving ? 'Saving…' : 'Save settings'}
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}

function Stat({ label, value, cls = 'text-stone-800 dark:text-stone-200', testId }) {
  return (
    <div className="bg-white dark:bg-muted rounded-xl p-3 shadow-sm border border-border/60" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={`text-base font-bold tabular-nums ${cls}`}>{value}</div>
    </div>
  );
}
